// Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Building2,
  Users,
  CreditCard,
  FileText,
  BarChart3,
  PieChart,
  Settings,
  Shield,
  User,
  LogOut,
  Bell,
  Search,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building,
  Landmark,
  DollarSign,
  Wallet,
  ChartBar,
  Eye,
  Upload,
  Download,
  Key,
  Mail,
  MessageCircle,
  Calculator,
  Calendar,
  TrendingUp,
  Briefcase,
  MapPin,
  Lock,
  Cog,
  FileCheck,
  Users as UsersIcon,
  ShieldCheck,
  Banknote,
  Coins,
  Award,
  Target,
  Zap
} from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [userData, setUserData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const userType = localStorage.getItem('userType') || 'Admin';

  useEffect(() => {
    // Load user data
    const user = {
      name: localStorage.getItem('userName') || 'Admin User',
      role: localStorage.getItem('userRole') || 'Administrator',
      email: localStorage.getItem('email') || 'admin@example.com',
      avatar: localStorage.getItem('avatar') || null
    };
    setUserData(user);
    
    // Load notifications (mock data)
    setNotifications([
      { id: 1, message: 'New plot payment received', time: '2 min ago', read: false },
      { id: 2, message: 'Monthly report ready', time: '1 hour ago', read: false },
      { id: 3, message: 'System update completed', time: 'Yesterday', read: true }
    ]);
  }, []);

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/Dashboard',
      show: true
    },
    {
      id: 'system-settings',
      label: 'System Settings',
      icon: Settings,
      show: userType === 'Admin',
      submenu: [
        { label: 'Register Company', path: '/CreateCompanyForm', icon: Building },
        { label: 'Register Business Location', path: '/CreateBusinessLocationForm', icon: MapPin }
      ]
    },
    {
      id: 'business-settings',
      label: 'Business Settings',
      icon: Cog,
      show: userType !== 'Customer' && userType !== 'Tenant',
      submenu: [
        { label: 'Register Plots', path: '/UploadPlotsForm', icon: Landmark },
        { label: 'Register Houses', path: '/UploadHousesForm', icon: Building2 },
        { label: 'Register Users', path: '/UploadUsersForm', icon: Users },
        { label: 'Register Plot Customers', path: '/UploadPlotsCustomersForm', icon: UsersIcon },
        { label: 'Register Tenants', path: '/UploadTenantsForm', icon: Users },
        { label: 'Create Expenses', path: '/CreateExpenseForm', icon: DollarSign },
        { label: 'Register Rentals Expense', path: '/UploadRentalsExpenseForm', icon: Wallet },
        { label: 'Register Plots Expense', path: '/UploadPlotsExpenseForm', icon: CreditCard }
      ]
    },
    {
      id: 'system-reports',
      label: 'System Reports',
      icon: FileText,
      show: userType !== 'Customer' && userType !== 'Tenant',
      submenu: [
        { label: 'View Plots', path: '/ViewPlots', icon: Eye },
        { label: 'View Houses', path: '/ViewHouses', icon: Eye },
        { label: 'View Plot Customers', path: '/PlotsCustomers', icon: UsersIcon },
        { label: 'View Sold & Unsold Plots', path: '/ViewSoldAndUnsoldPlots', icon: ChartBar },
        { label: 'View Users', path: '', icon: Users, hidden: true }
      ]
    },
    {
      id: 'client-statements',
      label: 'Client Statements',
      icon: BarChart3,
      show: userType !== 'Customer' && userType !== 'Tenant',
      submenu: [
        { label: 'House Tenants Statements', path: '/ViewTenants', icon: FileCheck },
        { label: 'Plot Customers Statements', path: '/ViewPlotsCustomers', icon: FileCheck },
        { label: 'UptoDate Plot Customers', path: '/ViewPlotsCustomersUptodatePayers', icon: Award },
        { label: 'Late Plot Customers', path: '/ViewPlotsCustomersLatePayers', icon: Target },
        { label: 'Customers Paid In Full', path: '/ViewPlotsCustomersCompletedPayers', icon: ShieldCheck },
        { label: 'NHEF', path: '/ViewPlotsCustomersNHEF', icon: Banknote }
      ]
    },
    {
      id: 'payments',
      label: 'Pay For Client',
      icon: CreditCard,
      show: userType !== 'Customer' && userType !== 'Tenant',
      submenu: [
        { label: 'Pay For Plot', path: '/MakePaymentForClientPlotsForm', icon: Coins },
        { label: 'Pay Rentals', path: '/MakePaymentForClientRentalsForm', icon: Wallet }
      ]
    }
  ];

  const MenuItem = ({ item }) => {
    const isActive = location.pathname === item.path || 
                    (item.submenu && item.submenu.some(sub => sub.path === location.pathname));

    if (!item.show) return null;

    if (item.submenu) {
      return (
        <div className="mb-1">
          <button
            onClick={() => toggleMenu(item.id)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 ${
              isActive 
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center space-x-3">
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expandedMenus[item.id] ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedMenus[item.id] && (
            <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
              {item.submenu.map((subItem) => (
                !subItem.hidden && (
                  <Link
                    key={subItem.label}
                    to={subItem.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                      location.pathname === subItem.path
                        ? 'bg-blue-50 text-blue-600 font-medium border-l-2 border-blue-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {subItem.icon && <subItem.icon className="w-4 h-4" />}
                    <span>{subItem.label}</span>
                  </Link>
                )
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={item.path}
        className={`flex items-center space-x-3 px-4 py-3 rounded-xl mb-1 transition-all duration-300 ${
          isActive
            ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 h-16 flex items-center justify-between px-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">RealEstate<span className="text-blue-600">Pro</span></h1>
              <p className="text-xs text-gray-500">Management System</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
            />
          </div>

          {/* Notifications */}
          <div className="relative">
            <button className="p-2 rounded-full hover:bg-gray-100 relative">
              <Bell className="w-5 h-5 text-gray-600" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
          </div>

          {/* User Profile */}
          <div className="flex items-center space-x-3">
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-gray-900">{userData?.name}</p>
              <p className="text-xs text-gray-500">{userData?.role}</p>
            </div>
            <div className="relative">
              <button className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {userData?.name?.charAt(0) || 'A'}
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm">
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white shadow-2xl animate-slideIn">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">RealEstate<span className="text-blue-600">Pro</span></h2>
                    <p className="text-sm text-gray-500">{userData?.role}</p>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {userData?.name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{userData?.name}</p>
                  <p className="text-sm text-gray-500">{userData?.email}</p>
                </div>
              </div>
            </div>

            <div className="p-4 overflow-y-auto h-[calc(100vh-200px)]">
              <nav className="space-y-1">
                {menuItems.map((item) => (
                  <MenuItem key={item.id} item={item} />
                ))}
                
                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 mt-8"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-gray-200 z-40 overflow-y-auto">
        <div className="p-6">
          <div className="mb-8">
            <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                {userData?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{userData?.name}</p>
                <p className="text-xs text-gray-500 truncate">{userData?.email}</p>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            {menuItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 mt-8"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </nav>

          {/* Quick Stats */}
          <div className="mt-12 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Storage</span>
              <span className="text-sm text-blue-600 font-semibold">65%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full" style={{ width: '65%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Documents & Reports</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Header;